import {
  pick, omitBy, isNil,
} from 'lodash'
import ThemeApi from '@/api/theme'
import theme, { DEFAULT_THEME_ATTRIBUTES } from '@/constant/theme'
import { isFeatureEnabled } from '@/vuex/modules/featureToggles.module'

const THEME_ATTRIBUTES = ['themeName', 'textColour', 'baseColour', 'visibility', 'backgroundImage', 'themeThumbnail', 'themeId', 'themeActive', 'colorPalette', 'logo', 'fontFamily']
const _state = {
  themes: [],
  officialThemes: [],
  teamThemes: [],
  isCreating: false,
  previewingTheme: null,
}

const EMPTY_THEMES_VALUES = {
}

export const nullifiedThemeEmptyField = (slide) => {
  const nullifiedSlide = {
    ...slide,
    textColour: slide.textColour === '' ? null : slide.textColour,
    baseColour: slide.baseColour === '' ? null : slide.baseColour,
    visibility: slide.visibility === -1 ? null : slide.visibility,
  }

  return omitBy(nullifiedSlide, isNil)
}

const actions = {
  getThemesAction: async ({ commit, getters }) => {
    if (!getters.canCreateTheme) return []
    const themes = await ThemeApi.getThemes()
    commit('setThemes', themes)
  },
  getOfficialThemesAction: async ({ commit, getters }) => {
    if (!getters.canViewThemeGallery) return []
    const officialThemes = await ThemeApi.getOfficialThemes()
    commit('setOfficialThemes', officialThemes)
  },
  getTeamThemesAction: async ({ commit, getters }) => {
    if (!getters.canViewThemeGallery) return []
    const teamThemes = await ThemeApi.getTeamThemes()
    commit('setTeamThemes', teamThemes)
  },
  createThemesAction: async ({ getters, commit }, payload) => {
    if (!getters.canCreateTheme) return {}
    try {
      commit('setIsCreating', true)
      const createdTheme = await ThemeApi.createThemes(payload)
      commit('setThemes', [createdTheme, ...getters.themes])
      return createdTheme
    } finally {
      commit('setIsCreating', false)
    }
  },
  deleteThemeAction: async ({ getters, commit }, id) => {
    if (!getters.canCreateTheme) return
    const remainThemes = getters.themes?.filter((_theme) => {
      return _theme.id !== id
    })
    commit('setThemes', remainThemes)
    await ThemeApi.deleteTheme(id)
  },
  setThemeAction: async ({ commit }, payload) => {
    const themeAttribute = pick(payload, THEME_ATTRIBUTES)

    // Optimistic update first
    Object.keys(themeAttribute).forEach((key) => {
      commit('presentationAttributeModule/updatePresentationAttribute', {
        attributeName: key,
        attributeValue: themeAttribute[key],
      }, { root: true })
    })
    await ThemeApi.updatePresentationTheme(payload.presentationId, {
      ...themeAttribute,
      backgroundImage: themeAttribute.backgroundImage?.name,
      logo: themeAttribute.logo?.name,
    })
  },
  setPreviewThemeAction: async ({ commit }, selectedTheme) => {
    commit('setPreviewTheme', selectedTheme)
  },
  toggleShareThemeAction: async ({ state, getters, commit }, { id, isShare }) => {
    const updateThemes = getters.themes?.map((_theme) => {
      if (_theme.id === id) _theme.isShareWithTeam = isShare
      return _theme
    })

    commit('setThemes', updateThemes)
    await ThemeApi.updateTheme(id, { isShareWithTeam: isShare })

    if (isShare) {
      const updateTheme = state.themes.find((_theme) => _theme.id === id)
      commit('setTeamThemes', state.teamThemes.concat(updateTheme))
    } else {
      const remainThemes = state.teamThemes.filter((_theme) => _theme.id !== id)
      commit('setTeamThemes', remainThemes)
    }
  },
}

const _getters = {
  canUseTheme: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-theme')
  },
  canCreateTheme: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-create-theme')
  },
  canViewThemeGallery: (_, __, ___, rootGetters) => {
    return isFeatureEnabled(rootGetters, 'release-theme-gallery')
  },
  currentPresentationTheme: (_, getters, rootState) => {
    const attribute = rootState.presentationAttributeModule.presentationAttribute
    const themeAttribute = pick(attribute, THEME_ATTRIBUTES)
    // Front-end stored themes
    // Should be remove when theme gallery and custom theme feature are 100% released
    const frontEndTheme = theme.presentations.find((t) => t.themeId === themeAttribute.themeId)
    const { previewingTheme } = getters
    if (frontEndTheme) {
      return {
        ...DEFAULT_THEME_ATTRIBUTES,
        ...frontEndTheme,
        ...previewingTheme,
      }
    }

    return {
      ...DEFAULT_THEME_ATTRIBUTES,
      ...themeAttribute,
      ...previewingTheme,
    }
  },
  isCreating: (state) => state.isCreating,
  themes: (state) => state.themes,
  // make another alias for themes
  themeList: (state) => state.themes?.map((t) => omitBy(t, isNil)),
  officialThemes: (state) => state.officialThemes?.map((t) => omitBy(t, isNil)),
  allThemes: (state) => [...state.themes, ...state.officialThemes],
  teamThemes: (state) => state.teamThemes,
  previewingTheme: (state) => state.previewingTheme,
  defaultThemeValues: (_, getters, rootState) => {
    if (getters.canViewThemeGallery) return EMPTY_THEMES_VALUES
    const slideActive = rootState?.slideActive
    return {
      textColour: slideActive?.textColour || DEFAULT_THEME_ATTRIBUTES.textColour,
      baseColour: slideActive?.baseColour || DEFAULT_THEME_ATTRIBUTES.baseColour,
      visibility: slideActive?.visibility,
      backgroundImage: slideActive?.backgroundImage,
    }
  },

}

const mutations = {
  setThemes(state, payload) {
    state.themes = payload
  },
  setOfficialThemes(state, payload) {
    state.officialThemes = payload
  },
  setIsCreating(state, payload) {
    state.isCreating = payload
  },
  setTeamThemes(state, payload) {
    state.teamThemes = payload
  },
  setPreviewTheme(state, payload) {
    state.previewingTheme = payload
  },
}

export default {
  namespaced: true,
  state: _state,
  actions,
  mutations,
  getters: _getters,
}
